/**
 * 🆕 [Phase 56 BT — P2.0] Composants utilitaires extraits de `EditerBonTravailDialog.jsx`
 * (et également utilisables par `CreerBonTravailDialog.jsx` si besoin).
 *
 * Ces composants sont pleinement autonomes (pas de dépendance sur le `form` /
 * `setForm` du parent) — ils communiquent via props et callbacks `onChange`.
 *
 * - `IAValidationButton` : Bouton « Valider avec l'IA » qui appelle
 *   `POST /api/ia/valider-planning` et affiche le résultat (validation, alertes,
 *   suggestions). Aucun state externe modifié.
 *
 * - `NacelleSelector` : Sélecteur de nacelle qui charge l'inventaire Élévation
 *   au montage et auto-sélectionne la première si aucune n'est déjà choisie.
 *
 * Cible refactor : alléger `EditerBonTravailDialog.jsx` (3317 L → ~3160 L).
 */
import axios from 'axios';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API_URL } from './utils';
import { RefreshCw, Sparkles, CheckCircle2, AlertTriangle } from 'lucide-react';


/* ─── IAValidationButton ────────────────────────────────────────────────── */
export function IAValidationButton({ form, employees }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleValidate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const chef = employees?.find(e => e.id === form.chef_equipe_id);
      const equipements = (form.equipements_selectionnes || []).map(e => ({
        nom: e.equipement_nom || e.nom,
        date_montage: e.date_montage || null,
        date_demontage: e.date_demontage || null,
      }));
      const res = await axios.post(`${API_URL}/api/ia/valider-planning`, {
        action: 'modifier_bt',
        chef_id: form.chef_equipe_id || '',
        chef_nom: form.chef_equipe_nom || (chef ? `${chef.prenom} ${chef.nom}` : ''),
        dates: form.dates_chantier || [],
        lavage: form.lavage_pression_a_faire || false,
        echafauds: form.echafauds_a_monter || false,
        nacelle: form.nacelle_lavage_requise || false,
        equipements,
      });
      setResult(res.data);
    } catch (e) {
      toast.error('Erreur validation IA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="ia-validation">
      <Button
        type="button"
        onClick={handleValidate}
        disabled={loading || !form.dates_chantier?.length}
        variant="outline"
        className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
        size="sm"
      >
        {loading
          ? <><RefreshCw className="w-4 h-4 animate-spin" /> Analyse en cours...</>
          : <><Sparkles className="w-4 h-4" /> Valider avec l&apos;IA</>
        }
      </Button>
      {result && (
        <div className={`rounded-lg border p-3 space-y-2 text-sm ${result.valide ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-2">
            {result.valide
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              : <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
            }
            <p className={`font-semibold ${result.valide ? 'text-emerald-800' : 'text-red-800'}`}>
              {result.resume}
            </p>
          </div>
          {result.alertes?.length > 0 && (
            <ul className="space-y-1">
              {result.alertes.map((a, i) => (
                <li key={a.id || a._id || i} className="flex items-start gap-1.5 text-red-700">
                  <span className="shrink-0 mt-0.5">⚠</span> {a}
                </li>
              ))}
            </ul>
          )}
          {result.suggestions?.length > 0 && (
            <ul className="space-y-1 border-t border-current/20 pt-2">
              {result.suggestions.map((s, i) => (
                <li key={s.id || s._id || i} className="flex items-start gap-1.5 text-blue-700">
                  <span className="shrink-0 mt-0.5">💡</span> {s}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/* ─── NacelleSelector ─────────────────────────────────────────────────────── */
export function NacelleSelector({ selectedId, onChange, dates }) {
  const [nacelles, setNacelles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadNacelles = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/equipements-inventaire?actif=true`);
        const nacellesList = (res.data.equipements || []).filter(e => e.est_nacelle);
        setNacelles(nacellesList);

        // Auto-sélection : Si aucune nacelle n'est sélectionnée et qu'il y en a de disponibles
        if (!selectedId && nacellesList.length > 0) {
          const premiereNacelle = nacellesList[0];
          onChange(premiereNacelle.id, premiereNacelle.nom);
          toast.success(`Nacelle "${premiereNacelle.nom}" sélectionnée automatiquement`);
        }
      } catch (e) {
        console.error('Erreur chargement nacelles:', e);
        toast.error('Impossible de charger les nacelles');
      } finally {
        setLoading(false);
      }
    };
    loadNacelles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ne s'exécute qu'au montage
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-cyan-600">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Chargement des nacelles...</span>
      </div>
    );
  }

  if (nacelles.length === 0) {
    return (
      <div className="text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-2">
        ⚠️ Aucune nacelle configurée dans l&apos;inventaire Élévation
      </div>
    );
  }

  return (
    <Select value={selectedId} onValueChange={(val) => {
      const nacelle = nacelles.find(n => n.id === val);
      onChange(val, nacelle?.nom || '');
    }}>
      <SelectTrigger className="h-9 border-cyan-200 focus:ring-cyan-400">
        <SelectValue placeholder="Sélectionner une nacelle..." />
      </SelectTrigger>
      <SelectContent>
        {nacelles.map(n => (
          <SelectItem key={n.id} value={n.id}>
            <div className="flex items-center gap-2">
              <span>🏗️</span>
              <span className="font-medium">{n.nom}</span>
              {n.numero_unite && <span className="text-xs text-cyan-600">#{n.numero_unite}</span>}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
